import { useEffect, useRef, useState } from 'react';
import './landing.css';
import { SalifyLogoMark } from './Logo';
import { Nav } from './Nav';

export type AppPage = 'home' | 'features' | 'how-it-works' | 'pricing' | 'terms' | 'privacy' | 'changelog';

interface LandingPageProps {
  onNavigate: (page: AppPage) => void;
  onGetStarted: () => void;
  onLogin: () => void;
}

const PLATFORMS = ['Gmail', 'LinkedIn', 'Salesforce', 'HubSpot', 'Outreach', 'Gong', 'Notion', 'WhatsApp'];

const OUTCOMES = [
  {
    title: 'Guided Conversations',
    body: 'Salifly doesn\'t just generate text; it understands the intent. Guide every conversation toward your goal with context-aware responses.',
    icon: '🎯',
    color: '#7C3AED',
  },
  {
    title: 'Industry-Specific Goals',
    body: 'Whether you\'re booking appointments in real estate or closing enterprise SaaS deals, Salifly adapts its logic to your specific industry outcomes.',
    icon: '🏢',
    color: '#2563EB',
  },
  {
    title: 'High-Performance Responses',
    body: 'Stop sending generic replies. Send sharp, calibrated responses that move the needle and maintain your authentic voice.',
    icon: '⚡',
    color: '#D97706',
  },
];

const RESPONSES = [
  { label: 'Response 01', text: "Alex, 18% close rate drop is exactly what we fix. Reps using Salifly average +31% in 90 days. Can I show you the data on Thursday?" },
  { label: 'Response 02', text: "That kind of board pressure is real. Before I pitch anything: what's the biggest gap in your team's follow-up flow today?" },
  { label: 'Response 03', text: "Companies at that stage usually find the issue is follow-up timing, not pipeline volume. Worth 15 minutes before your next board review?" },
];

const CHAPTERS = [
  {
    label: 'The reality',
    headline: 'Every deal has a moment.',
    sub: "A single message — sent too late, too generic, or too salesy. That's all it takes to lose it.",
  },
  {
    label: 'The insight',
    headline: 'The right reply already exists.',
    sub: "It's buried in the thread. In the objections. In the silence between their messages.",
  },
  {
    label: 'The resolution',
    headline: 'Salifly reads the room.',
    sub: 'Every conversation. Every signal. Three quality responses that keep deals moving forward — even when reps get lazy or rushed.',
  },
];

const STATS = [
  { value: 3, suffix: '×', label: 'faster', sub: 'reply drafting vs. writing from scratch' },
  { value: 3, suffix: '', label: 'drafts per analysis', sub: 'tone-matched for every lead' },
  { value: 16, suffix: '+', label: 'platforms', sub: 'Gmail, LinkedIn, Salesforce, and more' },
];

const MOATS = [
  {
    num: '01',
    tag: 'Hybrid Context',
    title: 'Dual payload defeats AI blindness.',
    body: 'Most AI tools see one input. Salifly sends two simultaneously: a spatial screenshot for layout, identity, and UI anchors — plus the full DOM-scraped thread for every message, objection, and buying signal. Vision reads where you are. Text reads what was said. Together they eliminate the blind spots that make single-input AI unreliable.',
    specs: [
      'Screenshot → lead identity, CRM layout, UI anchor points',
      'DOM thread → authoritative message content, objections, deal state',
      'Cross-source correlation drives high-confidence lead extraction',
      'Confidence indicator: "high" vs "review" surfaced in the UI',
    ],
  },
  {
    num: '02',
    tag: '2-Layer Privacy',
    title: 'Your conversation text is masked before it moves.',
    body: 'Two independent privacy layers protect your conversation text before it reaches any AI model. Layer 1 strips PII from the thread via client-side regex before it leaves your browser. Layer 2 re-runs the same patterns server-side inside each edge function before the AI ever sees it — defense in depth against missed masks. Screenshots are sent at full resolution to our secure AI servers for maximum visual accuracy.',
    specs: [
      'Layer 1 — Client regex: emails, phones, SSNs, FICO labels, card numbers, addresses',
      'Layer 2 — Server regex: same patterns re-applied inside edge functions before any model call',
      'Full-resolution screenshots sent to secure AI servers for maximum accuracy',
      'AI keys managed as edge function secrets — never stored in the extension',
    ],
  },
  {
    num: '03',
    tag: 'Universal Utility',
    title: 'Any platform. Any deal type. Zero setup.',
    body: 'Salifly auto-detects the platform from the DOM and adapts tone calibration for email, DM, or CRM note — with no configuration required. It is not tuned for one industry or one workflow. Sales reps in SaaS, real estate, healthcare, finance, automotive, and professional services all use the same extension. Context detection and playbook selection happen behind the scenes.',
    specs: [
      '16+ platforms: Gmail, LinkedIn, Salesforce, HubSpot, Outreach, Gong, and more',
      'Industry-aware playbooks: SaaS, real estate, healthcare, finance, automotive, services',
      'Auto-detected platform drives tone norms — email vs. DM vs. CRM note',
      'Zero configuration — analysis starts from any tab, any thread',
    ],
  },
  {
    num: '04',
    tag: 'Compounding Memory',
    title: 'It gets smarter every time you sell.',
    body: 'Salifly learns from every reply you send. On paid plans, it quietly extracts the durable facts about your business — pricing, plan tiers, hours, financing, warranty terms — and builds a private, per-rep knowledge base that every future draft draws on. Most AI tools start from zero on each message. Salifly compounds: the more you sell, the more it knows, and the more it sounds like you.',
    specs: [
      'Learns durable business facts from each sent reply, automatically',
      'Private per-rep knowledge base — never shared, never used to train any model',
      'Future drafts pull from what Salifly already knows about your business',
      'Unlimited on every paid plan — Basic, Pro, and Team',
    ],
  },
  {
    num: '05',
    tag: 'Follow-up Radar',
    title: 'Nothing goes cold without you knowing.',
    body: 'Most CRMs tell you a lead exists. None of them tell you when you are about to lose it. Salifly tracks the timestamp of your last interaction per lead and flags any conversation that has gone quiet for three or more days — sorted by deal-state urgency, so a stalling Negotiation always surfaces above a dormant Discovery. The extension icon shows a live badge count so you can triage across tabs without interrupting your current thread.',
    specs: [
      'Going-cold leads surface on the welcome screen by deal-state priority',
      'Extension icon badge shows a live count of stale conversations',
      'One click jumps straight back into the lead\'s thread',
      'Badge clears automatically once you re-analyze the lead',
    ],
  },
  {
    num: '06',
    tag: 'Win-Rate Intelligence',
    title: 'See which plays actually get replies — from your own history.',
    body: 'Generic AI writing tools give everyone the same output. Salifly goes further: every draft card shows your personal reply rate for that exact play angle, drawn from the drafts you have actually sent. Once you have at least three sends for a given tone label, the rate appears on the card and the star marks your top performer. Below that data floor it falls back to a neutral "picked N×" count — it never shows a rate it cannot stand behind.',
    specs: [
      'Reply rate keyed on the dynamic tone label of the draft you actually sent',
      'Star marks your highest reply-rate play in each generated set',
      'Minimum 3 sends before a reply rate is shown — no misleading sparse data',
      'Drawn from your own history only — never against other reps\' data',
    ],
  },
];

function WordReveal({ text, baseDelay = 0, className = '' }: { text: string; baseDelay?: number; className?: string }) {
  const words = text.split(' ');
  return (
    <span className={className} aria-label={text}>
      {words.map((word, i) => (
        <span
          key={i}
          className="st-word"
          style={{ '--wd': `${baseDelay + i * 0.055}s` } as React.CSSProperties}
        >
          {word}{i < words.length - 1 ? ' ' : ''}
        </span>
      ))}
    </span>
  );
}

function useCountUp(target: number, triggered: boolean, duration = 1600) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!triggered) return;
    let startTime = 0;
    const step = (ts: number) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      setValue(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [triggered, target, duration]);
  return value;
}

function StatCounter({ value, suffix, prefix = '', label, sub }: { value: number; suffix: string; prefix?: string; label: string; sub: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [triggered, setTriggered] = useState(false);
  const count = useCountUp(value, triggered);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setTriggered(true); obs.disconnect(); } },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div className="st-stat reveal" ref={ref}>
      <div className="st-stat-num">{prefix}{count}{suffix}</div>
      <div className="st-stat-label">{label}</div>
      <div className="st-stat-sub">{sub}</div>
    </div>
  );
}

export function LandingPage({ onNavigate, onGetStarted, onLogin }: LandingPageProps) {
  const chapterRef = useRef<HTMLElement>(null);
  const frameRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => e.isIntersecting && e.target.classList.add('is-visible')),
      { threshold: 0.07, rootMargin: '0px 0px -32px 0px' }
    );
    document.querySelectorAll('.reveal').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const section = chapterRef.current;
    if (!section) return;
    let ticking = false;

    const update = () => {
      const rect = section.getBoundingClientRect();
      const viewH = window.innerHeight;
      const scrollH = section.offsetHeight - viewH;
      if (scrollH <= 0) return;

      const progress = Math.max(0, Math.min(0.9999, -rect.top / scrollH));

      frameRefs.current.forEach((frame, i) => {
        if (!frame) return;
        const yPct = (i - progress * CHAPTERS.length) * 100;
        const clamped = Math.max(-110, Math.min(110, yPct));
        // translate3d keeps frames on their own GPU layer — no layout thrash during scroll
        frame.style.transform = `translate3d(0, ${clamped}%, 0)`;
        frame.style.opacity = String(Math.max(0, 1 - Math.abs(yPct) / 110));
      });
    };

    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => { update(); ticking = false; });
        ticking = true;
      }
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="lp st-lp">
      <Nav currentPage="home" onNavigate={onNavigate} onGetStarted={onGetStarted} onLogin={onLogin} />

      {/* ── Hero ── */}
      <section className="st-hero">
        <div className="st-orb st-orb--d" />
        <div className="st-orb st-orb--a" />
        <div className="st-orb st-orb--b" />
        <div className="st-orb st-orb--c" />

        <div className="st-hero-body">
          <p className="st-hero-slogan st-word" style={{ '--wd': '0s' } as React.CSSProperties}>
            Smarter sales, built into your browser.
          </p>

          <h1 className="st-hero-h1">
            <span className="st-hero-line">
              <WordReveal text="Close more deals." baseDelay={0.12} />
            </span>
            <span className="st-hero-line st-hero-line--glow">
              <WordReveal text="Write fewer messages." baseDelay={0.42} />
            </span>
          </h1>

          <p className="st-hero-sub st-word" style={{ '--wd': '0.88s' } as React.CSSProperties}>
            From three tailored responses to tone controls to phrase-level suggestions as you type — everything you need to send the right message, faster.
          </p>

          <div className="st-hero-cta st-word" style={{ '--wd': '1.12s' } as React.CSSProperties}>
            <button className="st-btn" onClick={onGetStarted}>
              Get started free
              <span className="st-btn-icon">→</span>
            </button>
            <button className="st-btn-ghost" onClick={() => onNavigate('how-it-works')}>
              See how it works
            </button>
          </div>

          <ul className="st-hero-proof st-word" style={{ '--wd': '1.35s' } as React.CSSProperties}>
            {['No credit card required', 'Free to start', '16+ platforms'].map(t => (
              <li key={t}><span className="st-proof-dot" />{t}</li>
            ))}
          </ul>
        </div>

        <div className="st-scroll-hint">
          <span className="st-scroll-line" />
        </div>
      </section>

      {/* ── Marquee ── */}
      <div className="st-marquee-wrap">
        <p className="st-marquee-label">Works everywhere you do</p>
        <div className="st-marquee">
          <div className="st-marquee-track">
            {[...PLATFORMS, ...PLATFORMS, ...PLATFORMS].map((name, i) => (
              <span key={i} className="st-marquee-item">{name}</span>
            ))}
          </div>
        </div>
      </div>

      {/* ── Scroll Chapter ── */}
      <section className="st-chapter-section" ref={chapterRef}>
        <div className="st-chapter-sticky">
          <div className="st-chapter-orb" />
          {CHAPTERS.map((ch, i) => (
            <div
              key={i}
              ref={el => { frameRefs.current[i] = el; }}
              className="st-chapter-frame"
            >
              <span className="st-chapter-label">{ch.label}</span>
              <h2 className="st-chapter-h2">{ch.headline}</h2>
              <p className="st-chapter-sub">{ch.sub}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Product Showcase ── */}
      <section className="st-product-section">
        <div className="st-product-inner">
          <div className="st-product-copy reveal">
            <span className="st-eyebrow">How it works</span>
            <h2 className="st-product-h2">
              Your conversations.<br />
              Your responses.<br />
              <em>Precisely.</em>
            </h2>
            <ul className="st-product-features">
              {[
                'Dual payload: screenshot + DOM thread eliminates AI blind spots',
                'Detects deal stage and objections from the full conversation',
                '2-layer text privacy: client mask → server mask, before any model call',
                'Streams 3 calibrated responses progressively — each grounded in deal stage, objections, and winning plays',
                'Works on 16+ platforms — any industry, zero configuration',
              ].map((f, i) => (
                <li key={i} className="st-product-feat">
                  <span className="st-feat-check">✓</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <button className="st-btn reveal reveal-delay-3" onClick={onGetStarted}>
              Try it free
              <span className="st-btn-icon">→</span>
            </button>
          </div>

          <div className="st-product-visual reveal reveal-delay-2">
            <div className="st-mock-outer">
              <div className="st-mock-inner">
                <div className="st-mock-topbar">
                  <SalifyLogoMark size={12} />
                  <span className="st-mock-wordmark">Salifly</span>
                  <span className="st-mock-live">● Context Synced</span>
                </div>
                <div className="st-mock-lead">
                  <div className="st-mock-avatar">AC</div>
                  <div>
                    <div className="st-mock-name">Alex Chen</div>
                    <div className="st-mock-role">VP Sales · Lattice</div>
                  </div>
                  <span className="chip chip-stalled" style={{ marginLeft: 'auto', fontSize: 9 }}>Stalled</span>
                </div>
                <div className="st-mock-thread">
                  <div className="st-mock-msg st-mock-msg--them">
                    "Our close rate dropped 18% and the board is asking questions."
                  </div>
                  <div className="st-mock-msg st-mock-msg--you">
                    "What shifted the priority this quarter?"
                  </div>
                </div>
                <div className="st-mock-drafts-lbl">3 RESPONSES READY</div>
                {RESPONSES.map((d, i) => (
                  <div key={i} className="st-mock-draft">
                    <span className="st-mock-tone">{d.label}</span>
                    <p className="st-mock-text">{d.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <section className="st-stats-section">
        <div className="st-stats-orb" />
        <div className="st-stats-inner">
          <span className="st-eyebrow st-eyebrow--dim reveal">By the numbers</span>
          <h2 className="st-stats-h2 reveal reveal-delay-1">The data doesn't lie.</h2>
          <div className="st-stats-grid">
            {STATS.map(s => <StatCounter key={s.label} {...s} />)}
          </div>
        </div>
      </section>

      {/* ── Technical Moats ── */}
      <section className="st-moats-section">
        <div className="st-moats-inner">
          <div className="st-moats-header reveal">
            <span className="st-eyebrow">Under the hood</span>
            <h2 className="st-moats-h2">
              Six technical moats.<br />Built into every analysis.
            </h2>
            <p className="st-moats-sub">
              The capabilities that separate Salifly from every other writing tool in your browser.
            </p>
          </div>
          <div className="st-moats-grid">
            {MOATS.map((m, i) => (
              <div key={m.num} className={`st-moat-card reveal reveal-delay-${i + 1}`}>
                <div className="st-moat-top">
                  <span className="st-moat-num">{m.num}</span>
                  <span className="st-moat-tag">{m.tag}</span>
                </div>
                <h3 className="st-moat-title">{m.title}</h3>
                <p className="st-moat-body">{m.body}</p>
                <ul className="st-moat-specs">
                  {m.specs.map(s => (
                    <li key={s} className="st-moat-spec">
                      <span className="st-moat-spec-dot" />
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Response Showcase ── */}
      <section className="st-drafts-section">
        <div className="st-drafts-inner">
          <span className="st-eyebrow reveal">What Salifly generates</span>
          <h2 className="st-drafts-h2 reveal reveal-delay-1">
            Three responses.<br />One click to send.
          </h2>
          <div className="st-drafts-grid">
            {RESPONSES.map((d, i) => (
              <div key={i} className={`st-draft-card reveal reveal-delay-${i + 1}`} data-tilt={i === 0 ? '-1' : i === 2 ? '1' : '0'}>
                <span className="st-draft-num">0{i + 1}</span>
                <p className="st-draft-body">{d.text}</p>
                <button className="st-draft-cta" onClick={onGetStarted}>Use this response</button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Inline Suggestions ── */}
      <section className="st-inline-section">
        <div className="st-inline-inner">
          <div className="st-inline-visual reveal">
            <div className="st-mock-outer">
              <div className="st-mock-inner st-inline-mock">
                <div className="st-inline-compose-bar">
                  <span className="st-inline-compose-dot" />
                  <span className="st-inline-compose-dot" />
                  <span className="st-inline-compose-dot" />
                  <span className="st-inline-compose-title">Gmail · Compose</span>
                </div>
                <div className="st-inline-compose-body">
                  <p className="st-inline-compose-text">
                    I just wanted to{' '}
                    <span className="st-sug-mark">touch base</span>
                    {' '}and{' '}
                    <span className="st-sug-mark">circle back</span>
                    {' '}on our previous conversation before we{' '}
                    <span className="st-sug-mark">move forward</span>.
                  </p>
                </div>
                <div className="st-inline-sug-badge">
                  <span className="st-inline-sug-num">3</span>
                  <span>suggestions</span>
                </div>
                <div className="st-inline-tip-card">
                  <div className="st-inline-tip-row">
                    <span className="st-inline-tip-cat">Tone</span>
                    <span className="st-inline-tip-phrase">"I just wanted to touch base and circle back on our previous conversation before we move forward."</span>
                  </div>
                  <div className="st-inline-tip-replace">→ "I am circling back on our discussion before moving forward."</div>
                  <p className="st-inline-tip-reason">Filler phrase. Specific timing drives action.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="st-inline-copy reveal reveal-delay-2">
            <span className="st-eyebrow">Inline suggestions</span>
            <h2 className="st-inline-h2">
              Weak phrases, caught<br />before you hit send.
            </h2>
            <p className="st-inline-sub">
              As you type, Salifly spots weak phrases — hedging language, filler words,
              corporate jargon — and underlines them in purple. Click to see a sharper
              replacement and exactly why it matters.
            </p>
            <ul className="st-product-features">
              {[
                'Detects hedging language, filler, and jargon in real time',
                'Shows the reason behind each suggestion — not just what, but why',
                'Works in Gmail compose, LinkedIn reply, and Salesforce fields',
                'Badge shows suggestion count without interrupting your flow',
              ].map((feat, i) => (
                <li key={i} className="st-product-feat">
                  <span className="st-feat-check">✓</span>
                  <span>{feat}</span>
                </li>
              ))}
            </ul>
            <button className="st-btn" style={{ marginTop: 32 }} onClick={onGetStarted}>
              Try it free
              <span className="st-btn-icon">→</span>
            </button>
          </div>
        </div>
      </section>

      {/* ── Outcomes ── */}
      <section className="st-testi-section">
        <div className="st-testi-header">
          <span className="st-eyebrow reveal">Every conversation has a goal</span>
          <h2 className="st-testi-h2 reveal reveal-delay-1">Salifly helps you reach it.</h2>
        </div>
        <div className="st-testi-grid">
          {OUTCOMES.map((o, i) => (
            <div key={o.title} className={`st-testi-card reveal reveal-delay-${i + 1}`}>
              <div className="st-testi-stars" style={{ fontSize: 24, marginBottom: 8 }}>{o.icon}</div>
              <p className="st-testi-name" style={{ fontSize: 18, marginBottom: 12 }}>{o.title}</p>
              <p className="st-testi-quote" style={{ fontStyle: 'normal' }}>{o.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="st-cta-section">
        <div className="st-cta-orb" />
        <div className="st-cta-body reveal">
          <span className="st-eyebrow st-eyebrow--dim">One install. Full context.</span>
          <h2 className="st-cta-h2">
            Stop staring at<br />a blank reply field.
          </h2>
          <p className="st-cta-sub">
            Salifly turns every conversation into context, and every context into a message that moves the deal forward.
          </p>
          <button className="st-btn st-btn--lg" onClick={onGetStarted}>
            Get started free
            <span className="st-btn-icon">→</span>
          </button>
          <button className="st-cta-secondary" onClick={onLogin}>
            Already have an account? Log in
          </button>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="st-footer">
        <div className="st-footer-inner">
          <div className="st-footer-top">
            <div className="st-footer-brand">
              <button className="nav-brand-btn" onClick={() => onNavigate('home')}>
                <span className="st-footer-logo">Salifly</span>
              </button>
              <p className="st-footer-tagline">Smarter sales, built into your browser.</p>
            </div>
            <div className="st-footer-cols">
              <div className="st-footer-col">
                <p className="st-footer-col-title">Product</p>
                <button className="st-footer-link" onClick={() => onNavigate('features')}>Features</button>
                <button className="st-footer-link" onClick={() => onNavigate('how-it-works')}>How it works</button>
                <button className="st-footer-link" onClick={() => onNavigate('pricing')}>Pricing</button>
              </div>
              <div className="st-footer-col">
                <p className="st-footer-col-title">Company</p>
                <a href="mailto:hello@salifly.com" className="st-footer-link">Contact</a>
                <a href="mailto:hello@salifly.com" className="st-footer-link">About</a>
              </div>
              <div className="st-footer-col">
                <p className="st-footer-col-title">Legal</p>
                <button className="st-footer-link" onClick={() => onNavigate('privacy')}>Privacy Policy</button>
                <button className="st-footer-link" onClick={() => onNavigate('terms')}>Terms of Service</button>
              </div>
            </div>
          </div>
          <div className="st-footer-bottom">
            <p className="st-footer-copy">© 2025 Salifly. All rights reserved.</p>
            <p className="st-footer-copy">Empowering sales reps everywhere.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
